import { useState, useCallback } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Paper
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/auth.service';
import { useAuth } from '@/hooks/useAuth';
import { usePedidoStore, type ClienteSelecionado } from '../../stores/pedido.store';
import { NovoClienteModal } from '../NovoClienteModal';

// AIDEV-NOTE: Step1 apenas seleciona o cliente — NAO avanca o step direto.
// O avanco (Step 0 → 1) acontece pelo botao "Avancar" no PedidoWizard,
// que cria o rascunho via POST /pedidos ANTES de mudar o step.

// AIDEV-NOTE: Resposta da API /api/clientes/busca - campos em UPPER_CASE vindos do Firebird
interface ClienteBuscaResult {
  CODIGO: number;
  RAZAO_SOCIAL: string;
  NOME_FANTASIA?: string | null;
  CNPJ_CPF: string;
  CIDADE: string;
  preco_varejo_atacado?: 'V' | 'A';
  LIMITE_DESCONTO_ESPECIAL?: number | null;
}

// AIDEV-NOTE: Debounce de 300ms na busca. Apenas busca com >= 3 caracteres.
// Usa TanStack Query para cache e deduplicacao automatica de requests.

// AIDEV-NOTE: Busca so de digitos e tratada como codigo do ERP e libera com 1 char
// (codigos tem 1-4 digitos). Mesma regra aplicada no server em /clientes/busca.
function buscaHabilitada(termo: string): boolean {
  if (termo.length === 0) return false;
  return /^\d+$/.test(termo) ? true : termo.length >= 3;
}

export function Step1ClienteSelect() {
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const [novoClienteOpen, setNovoClienteOpen] = useState(false);
  const setCliente = usePedidoStore((s) => s.setCliente);
  const clienteSelecionado = usePedidoStore((s) => s.clienteSelecionado);

  // AIDEV-NOTE: >= 3 chars para texto, 1 para codigo (debounce via staleTime + refetchOnMount)
  const searchEnabled = buscaHabilitada(inputValue.trim());

  const { data: clientes = [], isLoading } = useQuery({
    queryKey: ['clientes-busca', inputValue.trim()],
    enabled: searchEnabled,
    staleTime: 30_000,
    queryFn: async (): Promise<ClienteBuscaResult[]> => {
      const response = await api.get<{ clientes: ClienteBuscaResult[] }>('/clientes/busca', {
        params: { q: inputValue.trim() }
      });
      return response.data.clientes;
    }
  });

  const handleClienteSelect = useCallback(
    (_: unknown, value: ClienteBuscaResult | null) => {
      if (value) {
        const cliente: ClienteSelecionado = {
          CODIGO: value.CODIGO,
          RAZAO_SOCIAL: value.RAZAO_SOCIAL,
          CNPJ_CPF: value.CNPJ_CPF,
          CIDADE: value.CIDADE,
          preco_varejo_atacado: value.preco_varejo_atacado,
          limite_desconto_especial: value.LIMITE_DESCONTO_ESPECIAL ?? null
        };
        setCliente(cliente);
      }
    },
    [setCliente]
  );

  const handleNovoClienteCriado = useCallback(
    (cliente: { CODIGO: number; RAZAO_SOCIAL: string; CNPJ_CPF: string; CIDADE: string }) => {
      setCliente({
        CODIGO: cliente.CODIGO,
        RAZAO_SOCIAL: cliente.RAZAO_SOCIAL,
        CNPJ_CPF: cliente.CNPJ_CPF,
        CIDADE: cliente.CIDADE
      });
    },
    [setCliente]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Selecionar Cliente
      </Typography>

      <Autocomplete
        options={clientes}
        getOptionLabel={(option) =>
          `${option.CODIGO} - ${option.RAZAO_SOCIAL} - ${option.CNPJ_CPF} (${option.CIDADE})`
        }
        isOptionEqualToValue={(option, value) => option.CODIGO === value.CODIGO}
        loading={isLoading}
        inputValue={inputValue}
        onInputChange={(_, newValue, reason) => {
          if (reason !== 'reset') {
            setInputValue(newValue);
          }
        }}
        onChange={handleClienteSelect}
        noOptionsText={
          searchEnabled
            ? 'Nenhum cliente encontrado'
            : 'Digite o código do cliente ou pelo menos 3 caracteres'
        }
        renderOption={(props, option) => (
          <li {...props} key={option.CODIGO}>
            <Box>
              <Typography variant="body1" fontWeight={500}>
                {option.CODIGO} - {option.RAZAO_SOCIAL}
              </Typography>
              {option.NOME_FANTASIA && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {option.NOME_FANTASIA}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {option.CNPJ_CPF} - {option.CIDADE}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Buscar cliente por código, nome, CPF/CNPJ ou cidade"
            placeholder="Digite para buscar..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        PaperComponent={(props) => <Paper {...props} elevation={8} />}
      />

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {user?.pode_cadastrar_clientes && (
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => setNovoClienteOpen(true)}
          >
            Novo Cliente
          </Button>
        )}

        {clienteSelecionado && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Cliente selecionado:
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {clienteSelecionado.RAZAO_SOCIAL}
            </Typography>
          </Box>
        )}
      </Box>

      <NovoClienteModal
        open={novoClienteOpen}
        onClose={() => setNovoClienteOpen(false)}
        onClienteCriado={handleNovoClienteCriado}
      />
    </Box>
  );
}
